import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { exportDataLimit } from '@/constants';
import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type {
  CreateEstimationVersionRequest,
  CreateProjectWithEstimationRequest,
  EstimationAiAssistedReviewRequest,
  UpdateProjectWithEstimationRequest,
  UpsertEstimationAdditionalFieldRequest,
} from '../../schema/data-contracts';
import type { ApiEstimationMode } from '../../schema/data-contracts';
import { GetEstimationItemsByEstimationKey } from '../estimationItemQueryActions';
import {
  type EstimationsListParams,
  GetCurrentUserLatestEstimationQueryKey,
  type MutationProps,
} from '../types';

export const getEstimationQueryKey = 'estimation';
export const getEstimationsQueryKey = 'estimations';
export const getReviewCommentsSummaryQueryKey = 'review-comments-summary';
const getEstimationAdditionalFieldsKey = 'estimation-additional-fields';

export const useEstimationQueryActions = () => {
  const { auth } = useAuth();
  const {
    estimationController: {
      v1EstimationsList,
      v1EstimationsCreate,
      v1EstimationsUpdate,
      v1EstimationsDeleteWholeProjectDelete,
      v1EstimationsAdditionalFieldsUpdate,
      v1EstimationsAdditionalFieldsList,
      v1EstimationsApprovalCreate,
      v1EstimationsDetail,
      v1EstimationsAiReviewCreate,
      v1EstimationsReviewCreate,
      v1EstimationsAiReviewDetail,
      v1EstimationsReviewDelete,
      v1EstimationsReviewCommentsSummaryList,
      v1EstimationsVersionCreate,
      v1EstimationsInProcessCreate,
      v1EstimationsModeCreate,
    },
    estimationItemController: { v1EstimationsInitialEstimationItemsCreate },
  } = useApi();

  const queryClient = useQueryClient();

  const getEstimation = (estimationId: string) =>
    queryClient.fetchQuery({
      queryKey: [getEstimationQueryKey, estimationId],
      queryFn: async () => {
        const { data } = await v1EstimationsDetail(estimationId);
        return data;
      },
    });

  const useGetEstimation = (estimationId: string) =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: [getEstimationQueryKey, estimationId],
      queryFn: async () => {
        const { data } = await v1EstimationsDetail(estimationId);
        return data;
      },
    });

  // EWM-1138: `keepPreviousData` so a filter, search, sort or page change keeps the rows on
  // screen while the new page is in flight. Without it the query key changes, data goes
  // undefined, and the list empties: the table collapses, the page jumps, and the spinner ends
  // up centred in an empty container instead of over the rows being replaced. The overlay
  // loader is built for exactly this. Callers must key the loader on `isFetching` as well as
  // `isPending` - with previous data retained the query is no longer pending (see
  // useCurrentEstimates).
  const useGetEstimations = (params?: EstimationsListParams) =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: [getEstimationsQueryKey, params],
      placeholderData: keepPreviousData,
      queryFn: async () => {
        const { data } = await v1EstimationsList(params);
        return data;
      },
    });

  // TODO: make it queryClient aware
  const getEstimationsForExport = async (params?: EstimationsListParams) => {
    const response = await v1EstimationsList({ ...params, pageSize: exportDataLimit });
    return response;
  };

  const useGetEstimationAdditionalFields = (estimationId: string) =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: [getEstimationAdditionalFieldsKey, estimationId],
      queryFn: async () => {
        const { data } = await v1EstimationsAdditionalFieldsList(estimationId);
        return data;
      },
    });

  const useEnqueueEstimationAiReviewGeneration = (
    estimationId: string | undefined,
    props?: MutationProps,
  ) =>
    useMutation({
      ...props,
      mutationKey: ['enqueue-estimation-ai-review', estimationId],
      mutationFn: async (params: EstimationAiAssistedReviewRequest) => {
        if (!estimationId) throw new Error('Estimation ID is required');
        const { data } = await v1EstimationsAiReviewCreate(estimationId, params);
        return data;
      },
      onSuccess: async () => {
        // Invalidate all culture variants for this estimation's AI review
        await queryClient.invalidateQueries({ queryKey: ['latest-ai-review', estimationId] });
      },
    });

  const useGetLatestEstimationAiReview = (estimationId: string, cultureName: string) =>
    useQuery({
      enabled: Boolean(auth) && Boolean(estimationId) && Boolean(cultureName),
      queryKey: ['latest-ai-review', estimationId, cultureName],
      queryFn: async () => {
        const { data } = await v1EstimationsAiReviewDetail(estimationId, cultureName);
        return data;
      },
    });

  const useGetReviewCommentsSummary = (estimationId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && Boolean(estimationId),
      queryKey: [getReviewCommentsSummaryQueryKey, estimationId],
      queryFn: async () => {
        const response = await v1EstimationsReviewCommentsSummaryList(estimationId!);
        return response.data;
      },
    });

  const useCreateNewEstimation = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['create-estimation'],
      mutationFn: async (createProjectWithEstimation: CreateProjectWithEstimationRequest) => {
        const { data } = await v1EstimationsCreate(createProjectWithEstimation);
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: GetCurrentUserLatestEstimationQueryKey });
        await queryClient.invalidateQueries({ queryKey: [getEstimationsQueryKey] });
      },
    });

  const useUpdateEstimation = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['update-estimation'],
      mutationFn: async ({
        estimationId,
        updateProjectWithEstimation,
      }: {
        estimationId: string;
        updateProjectWithEstimation: UpdateProjectWithEstimationRequest;
      }) => {
        const { data } = await v1EstimationsUpdate(estimationId, updateProjectWithEstimation);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({ queryKey: GetCurrentUserLatestEstimationQueryKey });
        await queryClient.invalidateQueries({ queryKey: [getEstimationsQueryKey] });
        await queryClient.invalidateQueries({
          queryKey: [getEstimationQueryKey, variables.estimationId],
        });
      },
    });

  const useDeleteEstimation = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['delete-estimation'],
      mutationFn: async ({ estimationId }: { estimationId: string }) => {
        const { data } = await v1EstimationsDeleteWholeProjectDelete(estimationId);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({ queryKey: GetCurrentUserLatestEstimationQueryKey });
        await queryClient.invalidateQueries({ queryKey: [getEstimationsQueryKey] });
        await queryClient.invalidateQueries({
          queryKey: [getEstimationQueryKey, variables.estimationId],
        });
      },
    });

  const useCreateEstimationInitialEstimationItems = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['create-estimation-initial-estimation-items'],
      mutationFn: async (estimationId: string) => {
        const { data } = await v1EstimationsInitialEstimationItemsCreate(estimationId);
        return data;
      },
      onSuccess: async (_, estimationId) => {
        await queryClient.invalidateQueries({
          queryKey: [GetEstimationItemsByEstimationKey, estimationId],
        });
      },
    });

  const useUpdateEstimationAdditionalFields = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['update-additional-fields'],
      mutationFn: async ({
        estimationId,
        upsertEstimationAdditionalFields,
      }: {
        estimationId: string;
        upsertEstimationAdditionalFields: UpsertEstimationAdditionalFieldRequest[];
      }) => {
        const { data } = await v1EstimationsAdditionalFieldsUpdate(
          estimationId,
          upsertEstimationAdditionalFields,
        );
        return data;
      },
      onSuccess: async (_data, variables) => {
        await queryClient.invalidateQueries({
          queryKey: [getEstimationAdditionalFieldsKey, variables.estimationId],
        });
      },
    });

  const useApproveEstimation = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['approve-estimation'],
      mutationFn: async ({ estimationId }: { estimationId: string }) => {
        const { data } = await v1EstimationsApprovalCreate(estimationId);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === getEstimationQueryKey ||
            query.queryKey[0] === getEstimationsQueryKey,
        });
        await queryClient.invalidateQueries({
          queryKey: [getReviewCommentsSummaryQueryKey, variables.estimationId],
        });
      },
    });

  const useStartEstimationReview = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['start-estimation-review'],
      mutationFn: async ({ estimationId }: { estimationId: string }) => {
        const { data } = await v1EstimationsReviewCreate(estimationId);
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === getEstimationQueryKey ||
            query.queryKey[0] === getEstimationsQueryKey,
        });
      },
    });

  const useCancelEstimationReview = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['cancel-estimation-review'],
      mutationFn: async ({ estimationId }: { estimationId: string }) => {
        const { data } = await v1EstimationsReviewDelete(estimationId);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === getEstimationQueryKey ||
            query.queryKey[0] === getEstimationsQueryKey,
        });
        await queryClient.invalidateQueries({
          queryKey: [getReviewCommentsSummaryQueryKey, variables.estimationId],
        });
      },
    });

  const useCreateEstimationVersion = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['create-estimation-version'],
      mutationFn: async ({
        estimationId,
        versionData,
      }: {
        estimationId: string;
        versionData: CreateEstimationVersionRequest;
      }) => {
        const { data } = await v1EstimationsVersionCreate(estimationId, versionData);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          queryKey: [getEstimationQueryKey, variables.estimationId],
        });
        await queryClient.invalidateQueries({
          queryKey: [getEstimationsQueryKey],
        });
      },
    });

  const useSetEstimationStageToInProcess = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['set-estimation-stage-to-in-process'],
      mutationFn: async ({ estimationId }: { estimationId: string }) => {
        const { data } = await v1EstimationsInProcessCreate(estimationId);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          queryKey: [getEstimationQueryKey, variables.estimationId],
        });
        await queryClient.invalidateQueries({ queryKey: GetCurrentUserLatestEstimationQueryKey });
      },
    });

  const useSetEstimationMode = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['set-estimation-mode'],
      mutationFn: async ({
        estimationId,
        mode,
      }: {
        estimationId: string;
        mode: ApiEstimationMode;
      }) => {
        const { data } = await v1EstimationsModeCreate(estimationId, { mode });
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          queryKey: [getEstimationQueryKey, variables.estimationId],
        });
      },
    });

  return {
    useGetEstimation,
    getEstimation,
    useGetEstimations,
    getEstimationsForExport,
    useCreateNewEstimation,
    useUpdateEstimation,
    useDeleteEstimation,
    useCreateEstimationInitialEstimationItems,
    useUpdateEstimationAdditionalFields,
    useGetEstimationAdditionalFields,
    useApproveEstimation,
    useEnqueueEstimationAiReviewGeneration,
    useGetLatestEstimationAiReview,
    useGetReviewCommentsSummary,
    useStartEstimationReview,
    useCancelEstimationReview,
    useCreateEstimationVersion,
    useSetEstimationStageToInProcess,
    useSetEstimationMode,
  };
};
